import { Component, OnInit, signal } from '@angular/core';

import { RvSpinnerDirective } from 'src/app/components/loading/rv-spinner.directive';
import { SkeletonComponent } from 'src/app/components/skeleton/skeleton.component';
import { IntegrationsUserService } from 'src/app/settings/services/integrations/integrations-user/integrations-user.service';
import {
  ConnectedIntegrationRow,
  UserIntegrationRef,
} from 'src/app/settings/services/integrations/models/integrations.types';
import { IntegrationConnectedRowComponent } from '../integration-connected-row/integration-connected-row.component';

@Component({
  selector: 'integrations-user-section',
  templateUrl: './integrations-user-section.component.html',
  styleUrl: './integrations-user-section.component.scss',
  standalone: true,
  imports: [
    IntegrationConnectedRowComponent,
    SkeletonComponent,
    RvSpinnerDirective,
  ],
})
export class IntegrationsUserSectionComponent implements OnInit {
  /** Skeleton state for the first page load only. */
  readonly initialLoading = signal(true);
  /** Spinner state for reconnect/disconnect work after the user confirms. */
  readonly loading = signal(false);
  readonly integrations = this.integrationsUserService.integrations;

  constructor(private integrationsUserService: IntegrationsUserService) {}

  ngOnInit(): void {
    void this.load();
  }

  private async load(): Promise<void> {
    this.initialLoading.set(true);
    try {
      await this.integrationsUserService.load();
    } finally {
      this.initialLoading.set(false);
    }
  }

  async onReconnect(integration: ConnectedIntegrationRow): Promise<void> {
    // No confirm dialog — loading covers the OAuth reconnect + reload.
    this.loading.set(true);
    try {
      await this.integrationsUserService.reconnectIntegration(
        this.toRef(integration),
      );
    } finally {
      this.loading.set(false);
    }
  }

  async onDisconnect(integration: ConnectedIntegrationRow): Promise<void> {
    const row = await this.integrationsUserService.confirmDisconnectIntegration(
      this.toRef(integration),
    );
    if (!row) {
      return;
    }

    this.loading.set(true);
    try {
      await this.integrationsUserService.disconnectIntegration(row);
    } finally {
      this.loading.set(false);
    }
  }

  private toRef(integration: ConnectedIntegrationRow): UserIntegrationRef {
    return {
      id: integration.id,
      account: integration.account,
    };
  }
}
